import React from 'react';
import { Typography, Grid } from '@arco-design/web-react';
import { IconCode, IconBook, IconExperiment } from '@arco-design/web-react/icon';

const { Title, Text, Paragraph } = Typography;
const { Row, Col } = Grid;

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  delay?: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  gradient,
  delay = 0
}) => {
  return (
    <div className="feature-card" style={{ animationDelay: `${delay}ms` }}>
      {/* 背景装饰 */}
      <div
        style={{
          position: 'absolute',
          top: '-30%',
          right: '-30%',
          width: '150%',
          height: '150%',
          background: gradient,
          opacity: 0.04,
          borderRadius: '50%',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />

      {/* 图标 */}
      <div
        style={{
          width: '80px',
          height: '80px',
          borderRadius: 'var(--radius-lg)',
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '32px',
          boxShadow: `0 4px 16px ${gradient.replace('linear-gradient', 'rgba').replace(/rgb\([^)]+\)/g, (match) => {
            const rgb = match.match(/\d+/g);
            return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.2)`;
          }).replace(/\d+deg/, '135deg')}`,
          position: 'relative',
          zIndex: 2,
          transition: 'all 0.3s ease',
        }}
        className="feature-icon"
      >
        {React.cloneElement(icon as React.ReactElement, {
          style: { fontSize: 32, color: 'white' }
        })}
      </div>

      {/* 标题 */}
      <Title
        heading={3}
        style={{
          color: 'var(--text-primary)',
          marginBottom: '20px',
          fontSize: '1.5rem',
          fontWeight: 700,
          position: 'relative',
          zIndex: 2,
        }}
      >
        {title}
      </Title>

      {/* 描述 */}
      <Text
        style={{
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
          fontSize: '1rem',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {description}
      </Text>

      {/* 悬停时的光晕效果 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '0',
          height: '0',
          borderRadius: '50%',
          background: gradient,
          opacity: 0,
          transform: 'translate(-50%, -50%)',
          transition: 'all 0.6s ease',
          pointerEvents: 'none',
        }}
        className="feature-glow"
      />
    </div>
  );
};

interface FeaturesSectionProps {
  className?: string;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ className }) => {
  const features = [
    {
      icon: <IconCode />,
      title: '智能API',
      description: '提供丰富的REST API接口，支持向量搜索、知识点获取、智能问答等功能，轻松集成到您的应用中。',
      gradient: 'linear-gradient(135deg, #00D4FF 0%, #0099CC 100%)',
      delay: 0,
    },
    {
      icon: <IconBook />,
      title: '医疗知识库',
      description: '整合海量医疗政策数据、市场数据、临床信息，为医疗从业者提供精准的行业洞察和决策支持。',
      gradient: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
      delay: 200,
    },
    {
      icon: <IconExperiment />,
      title: 'AI模型服务',
      description: '基于先进的深度学习模型，提供智能问答、文本分析、数据挖掘等AI服务，助力医疗创新。',
      gradient: 'linear-gradient(135deg, #EC4899 0%, #F97316 100%)',
      delay: 400,
    },
  ];

  return (
    <section className={`features-section ${className || ''}`}>
      <div className="features-container">
        {/* 标题区域 */}
        <div className="features-header">
          <Title heading={2} className="features-title">
            核心功能
          </Title>
          <Paragraph className="features-subtitle">
            强大的AI能力，专业的医疗数据服务
          </Paragraph>
        </div>

        {/* 功能卡片网格 */}
        <Row gutter={[40, 40]}>
          {features.map((feature, index) => (
            <Col xs={24} sm={12} lg={8} key={index}>
              <FeatureCard {...feature} />
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

export default FeaturesSection;
